import cn from "classnames"
import usePreRender from "hooks/usePreRender"
import { IoSearch } from "react-icons/io5"
import { CoverageCard } from "./CoverageCard"

interface CoverageResultsProps {
  searchResults: any;
  selectedCoverages: Set<string>;
  expandedCards: Set<string>;
  currentPage: number;
  resultsPerPage: number;
  onCoverageSelect: (coverageId: string) => void;
  onSelectAll: () => void;
  onToggleExpansion: (cardId: string) => void;
  onCopyToClipboard: (text: string) => void;
  onPageChange: (page: number) => void;
  onClearSearch: () => void;
  onEdit: (cardId: string, updatedCoverage: any) => void;
  onDuplicate: (cardId: string, coverage: any) => void;
}

export const CoverageResults: React.FC<CoverageResultsProps> = ({
  searchResults,
  selectedCoverages,
  expandedCards,
  currentPage,
  resultsPerPage,
  onCoverageSelect,
  onSelectAll,
  onToggleExpansion,
  onCopyToClipboard,
  onPageChange,
  onClearSearch,
  onEdit,
  onDuplicate,
}) => {
  const { _color } = usePreRender()

  const getPaginationIndexes = () => {
    if (!searchResults) return { start: 0, end: 0 };
    
    const start = (currentPage - 1) * resultsPerPage + 1;
    const end = Math.min(start + resultsPerPage - 1, searchResults?.totalRecordsMatched);
    
    return { start, end };
  };

  const onEditCard = async (cardId: string, updatedCoverage: any) => {
    onEdit(cardId, updatedCoverage);
  };

  const onDuplicateCard = (cardId: string, coverage: any) => {
    onDuplicate(cardId, coverage);
  };

  if (!searchResults) return null;

  return (
    <div className="space-y-4 max-w-4xl mx-auto">
      {/* Pagination Info */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex justify-between items-center">
        <div className="text-sm text-gray-600 mx-2">
          Mostrando {getPaginationIndexes().start}-{getPaginationIndexes().end} de {searchResults?.totalRecordsMatched} resultados
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2 mx-8">
            {selectedCoverages.size > 0 && (
              <span className="text-sm text-gray-600">{selectedCoverages.size} seleccionadas</span>
            )}
            <button
              onClick={onSelectAll}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              {selectedCoverages.size === searchResults?.records?.length ? 'Deseleccionar todas' : 'Seleccionar todas'}
            </button>
          </div>
          <div className="flex items-center space-x-2 mx-2">
            <button
              onClick={() => onPageChange(Math.max(currentPage - 1, 1))}
              disabled={currentPage === 1}
              className={`px-3 py-1 rounded-md ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            >
              Anterior
            </button>
            <span className="px-3 py-1 bg-blue-600 text-white rounded-md">{currentPage}</span>
            <button
              onClick={() => onPageChange(currentPage < searchResults?.maxPages ? currentPage + 1 : currentPage)}
              disabled={currentPage >= searchResults?.maxPages}
              className={`px-3 py-1 rounded-md ${currentPage >= searchResults?.maxPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            >
              Siguiente
            </button>
          </div>
        </div>
      </div>
      
      {/* Results Cards */}
      <div className="space-y-4">
        {searchResults?.records?.map((coverage: any, index: number) => {
          const cardId = coverage.id || `card-${index}`;
          
          return (
            <CoverageCard
              key={cardId}
              coverage={coverage}
              cardId={cardId}
              isExpanded={expandedCards.has(cardId)}
              isSelected={selectedCoverages.has(cardId)}
              onToggleExpansion={onToggleExpansion}
              onSelect={onCoverageSelect}
              onCopyToClipboard={onCopyToClipboard}
              onSave={onEditCard}
              onDuplicate={onDuplicateCard}
            />
          );
        })}
      </div>
      
      {/* Empty State */}
      {searchResults?.records?.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <IoSearch className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">No se encontraron resultados</h3>
          <p className="text-gray-500 mb-4">Intenta ajustar los filtros de búsqueda para encontrar lo que estás buscando.</p>
          <button
            onClick={onClearSearch}
            className={cn(
              "px-4 py-2 text-sm font-medium rounded-md text-white",
              `bg_${_color} hover:bg_${_color}_dark focus:ring_${_color}`
            )}
          >
            Limpiar filtros
          </button>
        </div>
      )}
      
      {/* Pagination Controls (Bottom) */}
      {searchResults?.records?.length > 0 && searchResults?.maxPages > 1 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex justify-center">
          <div className="flex items-center space-x-1">
            <button
              onClick={() => onPageChange(1)}
              disabled={currentPage === 1}
              className={`px-3 py-1 rounded-md ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            >
              Primera
            </button>
            <button
              onClick={() => onPageChange(Math.max(currentPage - 1, 1))}
              disabled={currentPage === 1}
              className={`px-3 py-1 rounded-md ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            >
              Anterior
            </button>
            
            {/* Page Numbers */}
            {Array.from({ length: Math.min(5, searchResults?.maxPages) }, (_, i) => {
              const pageNum = currentPage <= 3 
                ? i + 1 
                : currentPage >= searchResults?.maxPages - 2 
                  ? searchResults?.maxPages - 4 + i 
                  : currentPage - 2 + i;
              
              if (pageNum <= 0 || pageNum > searchResults?.maxPages) return null;
              
              return (
                <button
                  key={pageNum}
                  onClick={() => onPageChange(pageNum)}
                  className={`px-3 py-1 rounded-md ${
                    currentPage === pageNum 
                      ? `bg_${_color} text-white` 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            
            <button
              onClick={() => onPageChange(currentPage < searchResults?.maxPages ? currentPage + 1 : currentPage)}
              disabled={currentPage >= searchResults?.maxPages}
              className={`px-3 py-1 rounded-md ${currentPage >= searchResults?.maxPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            >
              Siguiente
            </button>
            <button
              onClick={() => onPageChange(searchResults?.maxPages)}
              disabled={currentPage >= searchResults?.maxPages}
              className={`px-3 py-1 rounded-md ${currentPage >= searchResults?.maxPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            >
              Última
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
